/**
 * Whole-word matching for sticky audio (Persian/Arabic/Latin).
 * A word ends at a space, نیم‌فاصله (ZWNJ), or punctuation.
 * "گل" matches گل in "گل‌ها", "گل،", and "گُل".
 * A longer saved phrase such as "گل‌خانه" is applied first and keeps the whole word.
 * Letters joined with no separator (گلخانه) are not a match.
 */

/** Letters and digits. ZWNJ, spaces, punctuation, and e'rab are breaks. */
const WORD_CHAR = /[\p{L}\p{N}]/u;

/** Arabic/Persian harakat and Quranic marks. Ignored so گل matches گُل and گلِ. */
const DIACRITIC = /[\u064B-\u065F\u0670\u06D6-\u06ED]/u;

export interface StickyWordSpan {
  start: number;
  end: number;
}

/** Arabic yeh/kaf → Persian, same length, so match indexes stay aligned. */
export function foldStickyMatchText(text: string): string {
  return text.normalize('NFC').replace(/\u064A/g, '\u06CC').replace(/\u0643/g, '\u06A9');
}

export function isStickyAudioDiacritic(char: string | undefined): boolean {
  if (!char) return false;
  return DIACRITIC.test(char);
}

export function isStickyAudioWordChar(char: string | undefined): boolean {
  if (!char || isStickyAudioDiacritic(char)) return false;
  return WORD_CHAR.test(char);
}

/** Match key: yeh/kaf folded, e'rab removed. Length may be shorter than the source. */
export function foldStickyMatchKey(text: string): string {
  const normalized = foldStickyMatchText(text);
  let folded = '';
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i]!;
    if (isStickyAudioDiacritic(ch)) continue;
    folded += ch;
  }
  return folded;
}

function foldWithOrigin(text: string): { normalized: string; folded: string; origin: number[] } {
  const normalized = foldStickyMatchText(text);
  let folded = '';
  const origin: number[] = [];
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i]!;
    if (isStickyAudioDiacritic(ch)) continue;
    folded += ch;
    origin.push(i);
  }
  return { normalized, folded, origin };
}

export function isWholeWordAt(text: string, start: number, length: number): boolean {
  if (length <= 0 || start < 0 || start + length > text.length) return false;
  const before = start > 0 ? text[start - 1] : '';
  const after = start + length < text.length ? text[start + length] : '';
  return !isStickyAudioWordChar(before) && !isStickyAudioWordChar(after);
}

/**
 * Whole-word spans in the folded source string.
 * The span includes e'rab that sits on the matched letters, so گُل is underlined as a whole.
 */
export function findWholeWordSpans(text: string, phrase: string): StickyWordSpan[] {
  const source = foldWithOrigin(text);
  const needle = foldStickyMatchKey(phrase.trim());
  if (!needle) return [];

  const lowerText = source.folded.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const len = needle.length;
  const hits: StickyWordSpan[] = [];
  let idx = 0;

  while (idx <= lowerText.length - len) {
    const found = lowerText.indexOf(lowerNeedle, idx);
    if (found === -1) break;
    const before = found > 0 ? source.folded[found - 1] : '';
    const after = found + len < source.folded.length ? source.folded[found + len] : '';
    if (!isStickyAudioWordChar(before) && !isStickyAudioWordChar(after)) {
      const start = source.origin[found]!;
      const last = source.origin[found + len - 1]!;
      let end = last + 1;
      while (end < source.normalized.length && isStickyAudioDiacritic(source.normalized[end])) {
        end += 1;
      }
      hits.push({ start, end });
    }
    idx = found + 1;
  }

  return hits;
}

/** All whole-word start indexes of phrase in text (NFC, case-insensitive). */
export function findWholeWordOccurrences(text: string, phrase: string): number[] {
  return findWholeWordSpans(text, phrase).map((span) => span.start);
}

function lastNonDiacritic(text: string): string {
  const folded = foldStickyMatchText(text);
  for (let i = folded.length - 1; i >= 0; i--) {
    const ch = folded[i]!;
    if (!isStickyAudioDiacritic(ch)) return ch;
  }
  return '';
}

function firstNonDiacritic(text: string): string {
  const folded = foldStickyMatchText(text);
  for (let i = 0; i < folded.length; i++) {
    const ch = folded[i]!;
    if (!isStickyAudioDiacritic(ch)) return ch;
  }
  return '';
}

/** Previous visible non-diacritic character in document order before a text node. */
export function getPreviousTextChar(node: Text, root: HTMLElement): string {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const parent = (n as Text).parentElement;
      if (parent?.closest('.tip-icon-wrapper, button, script, style')) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  let prev = '';
  while (walker.nextNode()) {
    if (walker.currentNode === node) break;
    const ch = lastNonDiacritic(walker.currentNode.textContent ?? '');
    if (ch) prev = ch;
  }
  return prev;
}

/** Next visible non-diacritic character in document order after a text node. */
export function getNextTextChar(node: Text, root: HTMLElement): string {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const parent = (n as Text).parentElement;
      if (parent?.closest('.tip-icon-wrapper, button, script, style')) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  let passed = false;
  while (walker.nextNode()) {
    if (!passed) {
      if (walker.currentNode === node) passed = true;
      continue;
    }
    const ch = firstNonDiacritic(walker.currentNode.textContent ?? '');
    if (ch) return ch;
  }
  return '';
}

export function isWholeWordInTextNode(
  node: Text,
  root: HTMLElement,
  localStart: number,
  phraseLen: number,
  original: string
): boolean {
  return spanIsWholeWordInTextNode(node, root, { start: localStart, end: localStart + phraseLen }, original);
}

/** Whole-word check that keeps e'rab inside the span and still sees the next text node. */
export function spanIsWholeWordInTextNode(
  node: Text,
  root: HTMLElement,
  span: StickyWordSpan,
  original: string
): boolean {
  const before = span.start > 0 ? '' : getPreviousTextChar(node, root);
  const after = span.end < original.length ? '' : getNextTextChar(node, root);
  return !isStickyAudioWordChar(before) && !isStickyAudioWordChar(after);
}
